import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Snowflake, CheckCircle2, Truck, User, Shield, Save, Loader2, Calendar, ArrowRight, History,
} from "lucide-react";
import PortalHeader from "@/components/PortalHeader";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DollarSign, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { LastDayDateField, isLateSeasonDate } from "@/components/LastDayDateField";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const PROFILE_KEYS = [
  "name", "email", "phone", "mailing_address", "city", "state", "zip_code",
  "trailer_make", "trailer_model", "resort", "lot_number", "key_location",
  "last_day_at_resort", "notes",
];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [season, setSeason] = useState(null);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [history, setHistory] = useState([]);
  const [selections, setSelections] = useState({ selected_addons: [], water_filter: false, slideouts_in: true });
  const [quote, setQuote] = useState(null);
  const [tab, setTab] = useState("profile");
  const [lateDateAcknowledged, setLateDateAcknowledged] = useState(false);

  useEffect(() => {
    setProfile({ ...user });
    api.get("/customer/season").then((r) => {
      setSeason(r.data);
      if (r.data.selections) setSelections(r.data.selections);
      setQuote(r.data.base_quote);
    }).catch(() => {});
    api.get("/customer/history").then((r) => setHistory(r.data.history)).catch(() => {});
  }, [user]);

  const saveSelections = async (sel) => {
    setSelections(sel);
    try {
      const { data } = await api.put("/customer/selections", sel);
      setQuote(data.quote);
      toast.success(data.signed_up ? "Options updated — your worksheet was updated." : "Options saved.");
      setSeason((s) => s ? { ...s, base_quote: data.quote } : s);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleAddon = (key) => {
    const cur = selections.selected_addons || [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    saveSelections({ ...selections, selected_addons: next });
  };

  const upd = (k) => (v) => setProfile((p) => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    if (!(profile.key_location || "").trim()) return toast.error("Please enter the key location.");
    if (!profile.last_day_at_resort) return toast.error("Please select your last day at the resort.");
    if (isLateSeasonDate(profile.last_day_at_resort) && !lateDateAcknowledged
        && profile.last_day_at_resort !== user.last_day_at_resort) {
      return toast.error("Please reselect the date and acknowledge the late-season warning.");
    }
    setSaving(true);
    try {
      const payload = {};
      PROFILE_KEYS.forEach((k) => { payload[k] = profile[k] ?? ""; });
      payload.late_date_acknowledged = lateDateAcknowledged;
      const { data } = await api.put("/customer/profile", payload);
      setProfile({ ...data });
      await refreshUser();
      toast.success("Your information has been saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleAuto = async (val) => {
    try {
      await api.put("/customer/profile", { auto_enroll: val });
      setSeason((s) => ({ ...s, auto_enroll: val }));
      toast.success(val ? "Auto sign-up enabled." : "Auto sign-up disabled.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match.");
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed successfully.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-background">
      <PortalHeader />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-mono-label text-xs text-muted-foreground">Customer Dashboard</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-primary">
            Hi {profile.name?.split(" ")[0] || "there"} 👋
          </h1>
        </motion.div>

        {/* Season status */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="mt-6 overflow-hidden rounded-2xl border border-border bg-primary text-primary-foreground"
          data-testid="season-status-card"
        >
          <div className="flex flex-col items-start justify-between gap-4 p-7 sm:flex-row sm:items-center">
            <div>
              <div className="flex items-center gap-2">
                <Snowflake className="h-5 w-5" />
                <span className="font-mono-label text-xs opacity-80">Winterizing {season?.season_year || 2026}</span>
              </div>
              {season?.signed_up ? (
                <>
                  <h2 className="mt-3 font-heading text-2xl font-bold" data-testid="customer-season-heading">
                    {season.signup?.completion_status === "Completed" ? "Winterizing completed ✓" : "You're signed up! ✓"}
                  </h2>
                  <p className="mt-1 text-primary-foreground/80">
                    Total: {money(season.signup?.price_total)} · Status: {season.signup?.completion_status}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="mt-3 font-heading text-2xl font-bold">Not signed up yet</h2>
                  <p className="mt-1 text-primary-foreground/80">
                    Your price starts at {money(season?.base_quote?.total)}. Reserve your spot for the season.
                  </p>
                </>
              )}
            </div>
            {season?.signed_up ? (
              <Badge className="bg-success text-white" data-testid="signed-up-badge">
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                {season.signup?.completion_status === "Completed" ? "Completed" : "Signed Up"}
              </Badge>
            ) : (
              <Button
                data-testid="go-signup-btn"
                onClick={() => navigate("/signup")}
                className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              >
                Sign Up Now <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </motion.div>

        {/* Verify-options note */}
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          onClick={() => setTab("pricing")}
          data-testid="verify-options-note"
          className="mt-4 flex w-full items-center gap-3 rounded-xl border-2 border-secondary/40 bg-secondary/5 px-5 py-4 text-left transition-colors hover:bg-secondary/10"
        >
          <AlertCircle className="h-5 w-5 shrink-0 text-secondary" />
          <span className="text-sm text-foreground">
            <span className="font-semibold text-secondary">Please verify your service options.</span>{" "}
            Open the <span className="font-semibold">Pricing</span> tab to confirm your add-on services and pricing for this season.
          </span>
        </motion.button>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList data-testid="dashboard-tabs" className="h-auto w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="profile"><User className="mr-1.5 h-4 w-4" /> Profile & RV</TabsTrigger>
            <TabsTrigger value="pricing"><DollarSign className="mr-1.5 h-4 w-4" /> Pricing</TabsTrigger>
            <TabsTrigger value="prefs"><Calendar className="mr-1.5 h-4 w-4" /> Preferences</TabsTrigger>
            <TabsTrigger value="security"><Shield className="mr-1.5 h-4 w-4" /> Security</TabsTrigger>
            <TabsTrigger value="history"><History className="mr-1.5 h-4 w-4" /> History</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-primary">
                  <User className="h-4 w-4" /> Personal Information
                </h3>
                <div className="mt-4 space-y-4">
                  <Field label="Full name" testid="profile-name" value={profile.name} onChange={upd("name")} />
                  <Field label="Email" testid="profile-email" value={profile.email} onChange={upd("email")} />
                  <Field label="Phone" testid="profile-phone" value={profile.phone} onChange={upd("phone")} />
                  <Field label="Mailing address (street)" testid="profile-address" value={profile.mailing_address} onChange={upd("mailing_address")} />
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="City" testid="profile-city" value={profile.city} onChange={upd("city")} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="State" testid="profile-state" value={profile.state} onChange={upd("state")} />
                      <Field label="Zip" testid="profile-zip" value={profile.zip_code} onChange={upd("zip_code")} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-primary">
                  <Truck className="h-4 w-4" /> RV Information
                </h3>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Trailer make" testid="profile-make" value={profile.trailer_make} onChange={upd("trailer_make")} />
                    <Field label="Trailer model" testid="profile-model" value={profile.trailer_model} onChange={upd("trailer_model")} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Resort (trailer location)" testid="profile-resort" value={profile.resort} onChange={upd("resort")} />
                    <Field label="Lot #" testid="profile-lot" value={profile.lot_number} onChange={upd("lot_number")} />
                  </div>
                  <Field label="Key location (hiding spot)" testid="profile-key" value={profile.key_location} onChange={upd("key_location")} required />
                  <LastDayDateField value={profile.last_day_at_resort} onChange={upd("last_day_at_resort")}
                    onAcknowledgedChange={setLateDateAcknowledged} acknowledged={lateDateAcknowledged}
                    testId="profile-lastday" required />
                  <div className="space-y-2">
                    <Label>Additional notes</Label>
                    <Textarea data-testid="profile-notes" value={profile.notes || ""} onChange={(e) => upd("notes")(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
            <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving} className="mt-6 rounded-full">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Changes
            </Button>
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2" data-testid="pricing-tab">
                <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-primary">
                  <DollarSign className="h-4 w-4" /> Your Winterizing Pricing
                </h3>
                <div className="mt-4 rounded-lg bg-muted/40 px-4 py-3">
                  <p className="text-sm text-muted-foreground">
                    {season?.customer_specific_price != null ? "Your special base rate" : "Base winterizing price"}
                    <span className="ml-2 font-mono-label text-[9px] uppercase text-muted-foreground">(not editable)</span>
                  </p>
                  <p className="font-heading text-2xl font-bold text-primary" data-testid="pricing-base">
                    {money(quote?.base_component)}
                  </p>
                </div>

                <p className="mt-6 font-mono-label text-[10px] text-muted-foreground">Add-on services (affect your total)</p>
                <div className="mt-3 space-y-3">
                  {season?.pricing?.add_ons?.map((a) => (
                    <label key={a.key} data-testid={`pricing-addon-${a.key}`}
                      className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-4 py-3 transition-colors hover:bg-muted/40">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={(selections.selected_addons || []).includes(a.key)}
                          onCheckedChange={() => toggleAddon(a.key)} data-testid={`pricing-addon-check-${a.key}`} />
                        <span className="text-sm font-medium">{a.label}</span>
                      </div>
                      <span className="font-mono-label text-xs text-muted-foreground">+{money(a.price)}</span>
                    </label>
                  ))}
                </div>

                <p className="mt-6 font-mono-label text-[10px] text-muted-foreground">Additional information</p>
                <div className="mt-3 space-y-3">
                  <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-4 py-3" data-testid="pricing-water-filter-row">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={selections.water_filter}
                        onCheckedChange={(v) => saveSelections({ ...selections, water_filter: !!v })}
                        data-testid="pricing-water-filter-check" />
                      <span className="text-sm font-medium">Water Filter</span>
                    </div>
                    <span className="font-mono-label text-[10px] text-muted-foreground">No charge</span>
                  </label>
                  <div className="rounded-lg border border-border px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium">Slide-outs In?</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Toggle <span className="font-semibold">ON</span> if your slide-outs will be <span className="font-semibold">IN</span>; leave <span className="font-semibold">OFF</span> if <span className="font-semibold">OUT</span>.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-label text-[10px] text-muted-foreground">{selections.slideouts_in ? "IN" : "OUT"}</span>
                        {selections.slideouts_in && (
                          <span className="font-mono-label text-[10px] text-muted-foreground" data-testid="pricing-slideouts-charge-label">
                            +{money(season?.pricing?.slideouts_in_price)}
                          </span>
                        )}
                        <Switch checked={selections.slideouts_in}
                          onCheckedChange={(v) => saveSelections({ ...selections, slideouts_in: !!v })}
                          data-testid="pricing-slideouts-switch" />
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">Changes are saved automatically.</p>
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-24 rounded-2xl border border-border bg-primary p-6 text-primary-foreground">
                  <span className="font-mono-label text-xs opacity-80">Your Total · {season?.season_year}</span>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="opacity-80">{season?.customer_specific_price != null ? "Base (your rate)" : "Base"}</span><span className="font-medium">{money(quote?.base_component)}</span></div>
                    {quote?.addons?.map((a) => (
                      <div key={a.key} className="flex justify-between"><span className="opacity-80">{a.label}</span><span className="font-medium">+{money(a.price)}</span></div>
                    ))}
                    {selections.slideouts_in && quote?.slideouts_in && (
                      <div className="flex justify-between"><span className="opacity-80">Slide-outs In</span><span className="font-medium">+{money(quote.slideouts_in_price)}</span></div>
                    )}
                  </div>
                  <div className="mt-4 flex items-end justify-between border-t border-white/15 pt-4">
                    <span className="text-sm opacity-80">Total</span>
                    <span className="font-heading text-3xl font-bold" data-testid="pricing-total">{money(quote?.total)}</span>
                  </div>
                  {!season?.signed_up && (
                    <Button onClick={() => navigate("/signup")} data-testid="pricing-signup-btn"
                      className="mt-5 w-full rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90">
                      Continue to Sign Up
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="prefs" className="mt-6">
            <div className="max-w-xl rounded-xl border border-border bg-card p-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary">Automatic annual sign-up</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Automatically sign me up every year until I disable this option.
                  </p>
                </div>
                <Switch data-testid="auto-enroll-switch" checked={!!season?.auto_enroll} onCheckedChange={toggleAuto} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-6">
            <div className="max-w-md rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-lg font-semibold text-primary">Change password</h3>
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label>Current password</Label>
                  <Input type="password" data-testid="current-password-input" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>New password</Label>
                  <Input type="password" data-testid="new-password-input" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Confirm new password</Label>
                  <Input type="password" data-testid="confirm-password-input" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
                </div>
                <Button data-testid="change-password-btn" onClick={changePassword} className="rounded-full">Update Password</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-lg font-semibold text-primary">Winterizing history</h3>
              {history.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground" data-testid="history-empty">No signups yet.</p>
              ) : (
                <div className="mt-4 space-y-3" data-testid="history-list">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                      <span className="font-medium">Winterizing {h.season_year}</span>
                      <span className="text-sm text-muted-foreground">{money(h.price_total)} · {h.completion_status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <LegalFooter />
    </div>
  );
}

function Field({ label, value, onChange, testid, required = false }) {
  return (
    <div className="space-y-2">
      <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
      <Input data-testid={testid} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
